import type { SetStateFn } from '@/Types';
import { useAuthStore } from '@/Utility/auth/Auth';
import { useLocalState } from '@/Utility/hooks';
import { sanitize } from '@/Utility/utils';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Fade, FormControl, Input, InputAdornment, InputLabel } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { LoginProps } from '../login';
import { SSOLoginButton } from './sso-login-button';
import { type SSOLoginClient, navigateToSSOLoginUrl } from './sso.util';

const zodEmail = z.string().email();

export const LoginForm = (props: LoginProps & { loading: boolean; setLoading: SetStateFn<boolean> }) => {
  const [email, setEmail] = useLocalState<string>('auth/login:user_login', '');
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [togglePwd, setTogglePwd] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const signin = useAuthStore((state) => state.signin);

  const allowedEmailDomains = useMemo(() => {
    return [...new Set(props?.data?.loginClients?.flatMap((client) => client?.domains))].filter(
      (domain) => !!domain && domain !== '*'
    ) as string[];
  }, [props.data?.loginClients]);

  const { isEmailValidFromDomain, loginProviders } = useMemo(() => {
    if (step !== 'email') return { isEmailValidFromDomain: false, loginProviders: null };

    const value = email;
    const parsed = zodEmail.safeParse(value);
    if (parsed.success) {
      const endsInAllowedDomain = allowedEmailDomains.some((domain) => value.endsWith(`@${domain}`));

      const loginProviders = props.data?.loginClients
        ?.filter((client) => client?.domains?.some((domain) => value.endsWith(`@${domain}`)) && client?.isEnabled)
        ?.sort((a, b) => {
          if (a?.provider === 'GOOGLE_MULTI_TENANT' && b?.provider !== 'GOOGLE_MULTI_TENANT') return -1;
          if (a?.provider !== 'GOOGLE_MULTI_TENANT' && b?.provider === 'GOOGLE_MULTI_TENANT') return 1;
          return 0;
        });

      if (endsInAllowedDomain && loginProviders?.length) {
        return { isEmailValidFromDomain: true, loginProviders };
      }
    }
    return { isEmailValidFromDomain: false, loginProviders: null };
  }, [email, step]);

  const onSubmitHandler = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!isEmailValidFromDomain) {
        if (step === 'email') {
          setStep('password');

          setTimeout(() => {
            passwordRef.current?.focus();
          }, 250);
          return;
        }

        const formData = new FormData(e.currentTarget);
        props.setLoading(true);

        signin(formData.get('log') as string, formData.get('pwd') as string, (data, errors) => {
          props.setLoading(false);
          if (errors?.length) {
            toast.error(sanitize(errors.map((e) => e.message).join(' ')));

            setStep('email');
            setEmail('');
            setTogglePwd(false);
          }
        });

        return;
      }

      if (loginProviders?.length) {
        props.setLoading(true);

        await navigateToSSOLoginUrl(loginProviders[0] as SSOLoginClient, email);

        setTimeout(() => {
          props.setLoading(false);
        }, 1000);
      }
    },
    [email, isEmailValidFromDomain, props, setEmail, setStep, setTogglePwd, step, signin, loginProviders]
  );

  useEffect(() => {
    if (emailRef.current) {
      emailRef.current.focus();
    }

    return () => {
      setStep('email');
      setTogglePwd(false);
    };
  }, []);

  return (
    <form
      className="loginform form"
      onSubmit={onSubmitHandler}
      nonce={window.znRuntime?.cspStrictValue}
      aria-disabled={props?.isFallback}
    >
      <FormControl className="login-username">
        <InputLabel htmlFor="user_login">Username or Email</InputLabel>
        <Input
          id="user_login"
          type="text"
          name="log"
          size="small"
          autoComplete="off"
          placeholder="Username or Email"
          required
          inputRef={emailRef}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />
      </FormControl>

      <Fade in={step === 'password'}>
        {step === 'password' ? (
          <FormControl className="login-password">
            <InputLabel htmlFor="user_pass">Password</InputLabel>
            <Input
              id="user_pass"
              type={togglePwd ? 'text' : 'password'}
              name="pwd"
              size="small"
              required
              autoComplete="off"
              inputRef={passwordRef}
              endAdornment={
                <InputAdornment position="end">
                  {togglePwd ? (
                    <Visibility onClick={() => setTogglePwd(!togglePwd)} style={{ cursor: 'pointer' }} />
                  ) : (
                    <VisibilityOff onClick={() => setTogglePwd(!togglePwd)} style={{ cursor: 'pointer' }} />
                  )}
                </InputAdornment>
              }
            />
          </FormControl>
        ) : (
          <div />
        )}
      </Fade>

      <FormControl style={{ display: 'none' }}>
        <InputLabel htmlFor="cc-num" aria-label="hidden" />
        <Input name="cc-num" value="000" required id="cc-num" />
      </FormControl>

      <FormControl
        className="login-submit"
        disabled={props?.isFallback || props.loading}
        style={{
          ...(loginProviders?.length && { background: 'unset' }),
        }}
      >
        {!!loginProviders?.length ? (
          loginProviders.map((loginProvider, idx) => (
            <SSOLoginButton
              email={email}
              key={loginProvider?.provider}
              provider={loginProvider as SSOLoginClient}
              buttonProps={{
                style: { width: '100%', marginBottom: idx === loginProviders.length - 1 ? '0' : '4px' },
              }}
            />
          ))
        ) : (
          <Input
            id="wp-submit"
            type="submit"
            name="wp-submit"
            className="button button-primary"
            value={step === 'email' ? 'Continue' : 'Log In'}
            style={{ padding: '2px 26px' }}
          />
        )}
      </FormControl>
    </form>
  );
};
